import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface Shadow {
    x: number;
    y: number;
    blur: number;
    spread: number;
    color: string;
    inset: boolean;
}

interface State {
    shadows: Shadow[];
    bgColor: string;
    boxColor: string;
    copied: boolean;
}

export default class BoxShadowGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        shadows: [{ x: 0, y: 4, blur: 6, spread: -1, color: 'rgba(0,0,0,0.1)', inset: false }],
        bgColor: '#f8fafc',
        boxColor: '#ffffff',
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get cssValue(): string {
        return this.state.shadows.map(s =>
            `${s.inset ? 'inset ' : ''}${s.x}px ${s.y}px ${s.blur}px ${s.spread}px ${s.color}`,
        ).join(',\n            ');
    }

    private get cssCode(): string {
        return `box-shadow: ${this.cssValue};`;
    }

    update = (i: number, partial: Partial<Shadow>) => {
        const shadows = [...this.state.shadows];
        shadows[i] = { ...shadows[i], ...partial };
        this.setState({ shadows });
    };

    addShadow = () => {
        this.setState({ shadows: [...this.state.shadows, { x: 0, y: 8, blur: 16, spread: 0, color: 'rgba(0,0,0,0.15)', inset: false }] });
    };

    removeShadow = (i: number) => {
        if (this.state.shadows.length <= 1) return;
        this.setState({ shadows: this.state.shadows.filter((_, idx) => idx !== i) });
    };

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { shadows, bgColor, boxColor, copied } = this.state;
        const slider = (label: string, value: number, min: number, max: number, onChange: (v: number) => void) => (
            <div className={genStyles.field}>
                <label>{label}: <strong>{value}px</strong></label>
                <input type="range" min={min} max={max} value={value} onChange={e => onChange(+e.target.value)} />
            </div>
        );

        const controls = (
            <>
                {shadows.map((s, i) => (
                    <div key={i} className={genStyles.section}>
                        <div className={genStyles.sectionHeader}>
                            <span className={genStyles.sectionTitle}>Layer {i + 1}</span>
                            {shadows.length > 1 && (
                                <button className={genStyles.removeBtn} onClick={() => this.removeShadow(i)} aria-label="Remove layer">×</button>
                            )}
                        </div>
                        {slider('Offset X', s.x, -50, 50, v => this.update(i, { x: v }))}
                        {slider('Offset Y', s.y, -50, 50, v => this.update(i, { y: v }))}
                        {slider('Blur', s.blur, 0, 100, v => this.update(i, { blur: v }))}
                        {slider('Spread', s.spread, -30, 30, v => this.update(i, { spread: v }))}
                        <div className={genStyles.field}>
                            <label htmlFor={`bs-color-${i}`}>Color</label>
                            <input id={`bs-color-${i}`} type="text" value={s.color}
                                onChange={e => this.update(i, { color: e.target.value })} />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={s.inset} onChange={e => this.update(i, { inset: e.target.checked })} />
                            inset
                        </label>
                    </div>
                ))}
                <button className={genStyles.addBtn} onClick={this.addShadow}>+ Add layer</button>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Preview colors</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="bs-bg">Background</label>
                            <input id="bs-bg" type="color" value={bgColor}
                                style={{ height: 38, padding: 4 }}
                                onChange={e => this.setState({ bgColor: e.target.value })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="bs-box">Box</label>
                            <input id="bs-box" type="color" value={boxColor}
                                style={{ height: 38, padding: 4 }}
                                onChange={e => this.setState({ boxColor: e.target.value })} />
                        </div>
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                previewMeta={`${shadows.length} layer${shadows.length === 1 ? '' : 's'}`}
                preview={
                    <div style={{ background: bgColor, padding: 32, borderRadius: 8, width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: 160, height: 100, background: boxColor, borderRadius: 12, boxShadow: this.cssValue.replace(/\n\s+/g, ' ') }} />
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
